<script setup lang="ts">
/**
 * Health Check: a content problem (broken file, missing upload) opened in place — the item's preview, what's wrong,
 * which players reported it and where the item is used — so the list stays on screen while you work through it.
 */
import { computed, ref, watch } from 'vue';
import { MEDIA_STATUS_POLL_MS } from '@codot/contracts';
import { uploadMedia } from '../api/media-upload.api';
import { confirmDialog } from '../stores/confirm';
import { toast } from '../stores/toast';
import { useAuthStore } from '../stores/auth.store';
import { useI18n } from 'vue-i18n';
import type { ContentUsage, HealthIssue, LibraryType, MediaAssetRecord } from '@codot/contracts';
import { mediaApi } from '../api/content.api';
import { useLibraryBrowser, VISUAL_MEDIA } from '../utils/library-browser';
import { errorMessage } from '../api/http';
import { publishingApi } from '../api/publishing.api';
import { CODE_LABEL, issueMessage, since } from '../utils/health';
import ContentPreview from './content/ContentPreview.vue';
import SlideOverDrawer from './SlideOverDrawer.vue';
import ZoneWidget from './apps/ZoneWidget.vue';

const props = defineProps<{ issue: HealthIssue | null }>();
const emit = defineEmits<{ close: []; changed: [] }>();
const auth = useAuthStore();
const { t } = useI18n();

const asset = ref<MediaAssetRecord | null>(null);
const usage = ref<ContentUsage | null>(null);
const loading = ref(false);
const error = ref<string | null>(null);

watch(
  () => props.issue?.id,
  async () => {
    asset.value = null;
    usage.value = null;
    error.value = null;
    const i = props.issue;
    if (!i) return;
    loading.value = true;
    try {
      const [a, u] = await Promise.all([mediaApi.get(i.subjectId), publishingApi.usage({ kind: 'media', id: i.subjectId })]);
      asset.value = a;
      usage.value = u;
    } catch (err) {
      error.value = errorMessage(err);
    } finally {
      loading.value = false;
    }
  },
  { immediate: true },
);

/* ---------------- Fix it: replace with another item, upload a new file, or remove everywhere ---------------- */
const canFix = computed(() => auth.can('EDITOR') && !!asset.value && usedIn.value.length > 0);
const mode = ref<'pick' | 'upload' | null>(null);
const busy = ref(false);
const query = ref('');
watch(() => props.issue?.id, () => {
  mode.value = null;
  query.value = '';
  upload.value = null;
});
const isAudio = computed(() => asset.value?.kind === 'audio');
/** Ready items of the same sort (audio for audio), searched on the server while picking. */
const browser = useLibraryBrowser({
  q: query,
  types: computed<LibraryType[]>(() => (isAudio.value ? ['audio'] : VISUAL_MEDIA)),
  enabled: computed(() => mode.value === 'pick'),
});
const library = computed(() => (browser.loading.value && !browser.items.value.length ? null : browser.items.value));
const candidates = computed(() => (library.value ?? []).filter((a) => a.id !== asset.value?.id && a.status === 'READY').slice(0, 40));
function openPicker(): void {
  mode.value = 'pick';
}
/** "Changes 1 campaign, 2 players" — shown before every change. */
const impact = computed(() => {
  const u = usage.value;
  if (!u) return '';
  return [
    u.campaigns.length && t('notify.nCampaigns', { n: u.campaigns.length }, u.campaigns.length),
    u.playlists.length && t('notify.nPlaylists', { n: u.playlists.length }, u.playlists.length),
    u.players.length && t('notify.nPlayers', { n: u.players.length }, u.players.length),
  ].filter(Boolean).join(', ');
});
async function replaceWith(to: MediaAssetRecord, uploaded = false): Promise<void> {
  const from = asset.value!;
  if (!uploaded && !(await confirmDialog(t('notify.replaceQ', { from: from.name, to: to.name, impact: impact.value }), { okLabel: t('notify.replace') }))) return;
  busy.value = true;
  try {
    const r = await mediaApi.replace({ fromAssetId: from.id, toAssetId: to.id });
    toast(t('notify.replaced', { name: to.name, n: r.places }, r.places), {
      action: {
        label: t('health.undo'),
        run: async () => {
          await mediaApi.replace({ fromAssetId: to.id, toAssetId: from.id, only: r.changed });
          toast(t('notify.undone'));
          emit('changed');
        },
      },
    });
    emit('changed');
    emit('close');
  } catch (e) {
    toast(errorMessage(e), { tone: 'error' });
  } finally {
    busy.value = false;
  }
}
async function removeEverywhere(): Promise<void> {
  const a = asset.value!;
  if (!(await confirmDialog(t('notify.removeQ', { name: a.name, impact: impact.value }), { okLabel: t('notify.remove'), danger: true }))) return;
  busy.value = true;
  try {
    const r = await mediaApi.detach(a.id);
    toast(t('notify.removed', { name: a.name, n: r.players }, r.players));
    emit('changed');
    emit('close');
  } catch (e) {
    toast(errorMessage(e), { tone: 'error' });
  } finally {
    busy.value = false;
  }
}
/** Upload a new file: it becomes a new item (same name and folder), then takes the old one's place everywhere. */
const upload = ref<{ name: string; sent: number; stage: 'uploading' | 'optimizing'; optimize: number | null } | null>(null);
const fileInput = ref<HTMLInputElement | null>(null);
async function onFile(e: Event): Promise<void> {
  const file = (e.target as HTMLInputElement).files?.[0];
  (e.target as HTMLInputElement).value = '';
  const old = asset.value;
  if (!file || !old) return;
  if (!(await confirmDialog(t('notify.uploadQ', { file: file.name, name: old.name, impact: impact.value }), { okLabel: t('notify.replace') }))) return;
  upload.value = { name: file.name, sent: 0, stage: 'uploading', optimize: null };
  busy.value = true;
  try {
    let created = await uploadMedia(file, (f) => upload.value && (upload.value.sent = f), old.name, old.folderId).promise;
    upload.value.stage = 'optimizing';
    while (created.status !== 'READY') {
      if (created.status === 'FAILED') throw new Error(created.processingError ?? t('ui.notOptimized'));
      await new Promise((r) => setTimeout(r, MEDIA_STATUS_POLL_MS));
      created = await mediaApi.get(created.id);
      if (upload.value) upload.value.optimize = created.progress ?? null;
    }
    upload.value = null;
    await replaceWith(created, true);
  } catch (err) {
    upload.value = null;
    toast(errorMessage(err), { tone: 'error' });
  } finally {
    busy.value = false;
  }
}

const assets = computed(() => new Map(asset.value ? [[asset.value.id, asset.value]] : []));
const noCampaigns = new Map();
/** Players that reported the file (broken content) — names come with the issue. */
const reporters = computed(() => {
  const names = props.issue?.details.playerNames;
  return typeof names === 'string' && names ? names.split(',').map((n) => n.trim()) : [];
});
const reason = computed(() => {
  const r = props.issue?.details.reason;
  return typeof r === 'string' ? t(`health.why_${r}`, r.replace(/_/g, ' ')) : null;
});
const detail = computed(() => {
  const d = props.issue?.details.detail;
  return typeof d === 'string' && d ? d : null;
});
const usedIn = computed(() => {
  const u = usage.value;
  if (!u) return [];
  return [
    ...u.campaigns.map((c) => ({ key: `c-${c.id}`, label: c.name, kind: t('notify.inCampaign'), to: { name: 'campaign', params: { campaignId: c.id } } })),
    ...u.playlists.map((p) => ({ key: `p-${p.id}`, label: p.name, kind: t('notify.inPlaylist'), to: { name: 'playlist', params: { playlistId: p.id } } })),
    ...u.players.map((p) => ({ key: `s-${p.id}`, label: p.name, kind: t('notify.onPlayer'), to: { name: 'screen-content', params: { screenId: p.id } } })),
  ];
});
</script>

<template>
  <SlideOverDrawer :open="!!issue" :title="issue?.subjectName ?? ''" :description="issue ? CODE_LABEL[issue.code] : undefined" size="md" @close="emit('close')">
    <div v-if="issue" class="space-y-5 text-sm">
      <div class="overflow-hidden rounded-lg border border-slate-200 bg-slate-950 dark:border-slate-800">
        <ContentPreview v-if="asset" :key="asset.id" :target="{ kind: 'asset', asset }" :assets="assets" :campaigns="noCampaigns" />
        <div v-else class="flex aspect-video items-center justify-center text-xs text-slate-400">{{ loading ? t('notify.loading') : '—' }}</div>
      </div>

      <section class="rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-900 dark:bg-red-950/40" aria-labelledby="prob-h">
        <h3 id="prob-h" class="font-semibold text-red-800 dark:text-red-300">{{ t('notify.whatsWrong') }}</h3>
        <p class="mt-1 text-red-900 dark:text-red-200">{{ issueMessage(issue) }}</p>
        <dl class="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs text-red-900/80 dark:text-red-200/80">
          <template v-if="reason"><dt>{{ t('notify.reason') }}</dt><dd>{{ reason }}</dd></template>
          <template v-if="detail"><dt>{{ t('notify.detail') }}</dt><dd class="font-mono break-all" dir="ltr">{{ detail }}</dd></template>
          <dt>{{ t('notify.since') }}</dt><dd>{{ since(issue.openedAt) }}</dd>
          <template v-if="reporters.length"><dt>{{ t('notify.reportedBy') }}</dt><dd>{{ reporters.join(', ') }}</dd></template>
        </dl>
      </section>

      <section aria-labelledby="used-h">
        <h3 id="used-h" class="font-semibold">{{ t('notify.usedIn') }}</h3>
        <p v-if="error" class="mt-1 text-red-600">{{ error }}</p>
        <p v-else-if="loading" class="mt-1 text-slate-500">{{ t('notify.loading') }}</p>
        <p v-else-if="!usedIn.length" class="mt-1 text-slate-500">{{ t('notify.notUsed') }}</p>
        <ul v-else class="mt-2 divide-y divide-slate-100 rounded-lg border border-slate-200 dark:divide-slate-800 dark:border-slate-800">
          <li v-for="u in usedIn" :key="u.key">
            <RouterLink :to="u.to" @click="emit('close')" class="flex items-center gap-3 px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-800/60">
              <span class="w-20 shrink-0 text-xs text-slate-500">{{ u.kind }}</span>
              <span class="min-w-0 flex-1 truncate font-medium">{{ u.label }}</span>
              <span class="text-slate-400 rtl:rotate-180" aria-hidden="true">›</span>
            </RouterLink>
          </li>
        </ul>
      </section>

      <section v-if="canFix" aria-labelledby="fix-h" class="rounded-lg border border-indigo-200 p-3 dark:border-indigo-900">
        <h3 id="fix-h" class="font-semibold">{{ t('notify.fixIt') }}</h3>
        <p class="mt-0.5 text-xs text-slate-500">{{ t('notify.fixImpact', { impact }) }}</p>

        <div v-if="upload" class="mt-3 text-xs" role="status">
          <p class="font-medium">{{ upload.stage === 'uploading' ? t('notify.uploading', { name: upload.name }) : t('notify.preparing', { name: upload.name }) }}</p>
          <div class="mt-1.5 h-1.5 overflow-hidden rounded bg-slate-100 dark:bg-slate-800">
            <div class="h-full bg-indigo-600 transition-[width]" :style="{ width: `${Math.round((upload.stage === 'uploading' ? upload.sent : (upload.optimize ?? 0) / 100) * 100)}%` }" />
          </div>
        </div>

        <div v-else-if="mode === 'pick'" class="mt-3">
          <input v-model="query" type="search" class="input" :placeholder="t('notify.searchLibrary')" :aria-label="t('notify.searchLibrary')" />
          <p v-if="!library" class="mt-2 text-xs text-slate-500">{{ t('notify.loading') }}</p>
          <p v-else-if="!candidates.length" class="mt-2 text-xs text-slate-500">{{ t('notify.noCandidates') }}</p>
          <ul v-else class="mt-2 max-h-64 divide-y divide-slate-100 overflow-y-auto rounded-lg border border-slate-200 dark:divide-slate-800 dark:border-slate-800">
            <li v-for="c in candidates" :key="c.id">
              <button type="button" class="flex w-full items-center gap-3 px-2 py-1.5 text-start hover:bg-slate-50 disabled:opacity-50 dark:hover:bg-slate-800/60" :disabled="busy" @click="replaceWith(c)">
                <span class="relative flex h-9 w-14 shrink-0 items-center justify-center overflow-hidden rounded bg-slate-100 dark:bg-slate-800">
                  <img v-if="c.thumbnailUrl" :src="c.thumbnailUrl" alt="" class="h-full w-full object-cover" loading="lazy" />
                  <!-- Apps have no image: their own still preview, as in the library. -->
                  <ZoneWidget v-else-if="c.config" :config="c.config" :playing="false" :width="1920" :height="1080" class="pointer-events-none absolute inset-0" />
                  <span v-else class="text-base" aria-hidden="true">{{ c.kind === 'video' ? '🎬' : c.kind === 'app' ? '🧩' : c.kind === 'audio' ? '🎵' : '🖼️' }}</span>
                </span>
                <span class="min-w-0 flex-1 truncate">{{ c.name }}</span>
                <span class="text-xs text-slate-400">{{ c.kind }}</span>
              </button>
            </li>
          </ul>
          <button type="button" class="mt-2 text-xs text-slate-500 hover:underline" @click="mode = null">{{ t('common.cancel') }}</button>
        </div>

        <div v-else class="mt-3 grid gap-2">
          <button type="button" class="btn-primary justify-start" :disabled="busy" @click="openPicker">
            <span aria-hidden="true">⇄</span> {{ t('notify.replaceWith') }}
          </button>
          <button type="button" class="btn-secondary justify-start" :disabled="busy" @click="fileInput?.click()">
            <span aria-hidden="true">⤒</span> {{ t('notify.uploadNew') }}
          </button>
          <button type="button" class="btn-secondary justify-start text-red-700 dark:text-red-400" :disabled="busy" @click="removeEverywhere">
            <span aria-hidden="true">⊖</span> {{ t('notify.removeEverywhere') }}
          </button>
          <input ref="fileInput" type="file" class="hidden" :accept="isAudio ? 'audio/*' : 'image/*,video/*'" @change="onFile" />
        </div>
      </section>
      <p v-else class="text-xs leading-relaxed text-slate-500">{{ t('notify.fixHint') }}</p>
    </div>
    <template #footer>
      <div class="flex justify-end gap-2">
        <button type="button" class="btn-secondary" @click="emit('close')">{{ t('content.close') }}</button>
        <RouterLink v-if="issue" :to="{ name: 'dashboard', query: { edit: issue.subjectId } }" class="btn-primary" @click="emit('close')">{{ t('notify.openInLibrary') }}</RouterLink>
      </div>
    </template>
  </SlideOverDrawer>
</template>
